import { useEffect, useState } from 'react'
import { sendGet, sendPut, sendPost } from 'hooks/use-fetch'
import { IconButton, Stack, Typography } from '@mui/material'
import { Modal } from 'Components/Modal'
import { AttachMoney, FamilyRestroom, Edit } from '@mui/icons-material'
import { FormatCurrency } from 'core/Utilities'
import { TextFieldWithLabel } from 'Components/TextField'
import { toast } from 'react-toastify'
import { useRefresh, useRefreshDispatch } from 'ContextProviders/RefreshProvider'
import { type postError } from 'core/types'
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'
export interface ReportedIncomeProps {
  id: number | null
  childID: number | null // if id is null, then a record can be created for this child
  canEdit: boolean
}

interface IncomeRecord {
  id: number
  childID: number | null
  annualIncome: number | null
  memberCount: number | null
  fplPercent: number | null
}

export function ReportedIncome (props: ReportedIncomeProps): JSX.Element {
  const [reportedIncomeID, setReportedIncomeID] = useState<number | null>(props.id)
  const [details, setDetails] = useState<IncomeRecord | null>(null)
  const [editDetails, setEditDetails] = useState<IncomeRecord | null>(null)
  const [editMode, setEditMode] = useState(false)
  const disp = useRefreshDispatch()
  const setRefreshTime = (): void => { disp({ type: 'refresh' }) }
  const refresh = useRefresh()
  useEffect(() => {
    setReportedIncomeID(props.id)
  }, [props.id])
  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      if (reportedIncomeID != null) {
        const { response, error } = await sendGet(`/ReportedIncome/Find?id=${reportedIncomeID}`, {})
        if (error[0] === '') {
          setDetails(response)
        }
      }
    }
    void fetchData()
  }, [reportedIncomeID, props.id, refresh])

  const editIncome = (): void => {
    if (details === null) {
      setEditDetails({ id: 0, childID: props.childID, fplPercent: 0, annualIncome: null, memberCount: null })
    } else {
      setEditDetails(details)
    }
    setEditMode(true)
  }

  const closeModal = (): void => {
    setEditMode(false)
  }

  const onUpdate = async (): Promise<void> => {
    if (editDetails !== null) {
      let result
      if (details != null) {
        result = await sendPut('/ReportedIncome/Update', editDetails)
      } else {
        result = await sendPost('/ReportedIncome/Insert', editDetails)
      }
      const rsp: { success: boolean, errors: postError[], newRecordID: number | null } = result.response
      if (rsp.success) {
        if (details != null) {
          setDetails(editDetails)
        } else {
          setReportedIncomeID(result.response.newRecordID)
        }
        setEditMode(false)
        setRefreshTime()
      } else {
        rsp.errors.forEach((error) => {
          toast.error(error.error)
        })
      }
    }
  }

  return <>
    <Stack direction='row' alignItems='center' sx={{ border: '2px solid', borderColor: '#722A82', borderRadius: 3, p: 0.5, flexWrap: 'wrap', display: 'flex', columnGap: '5px', width: 'max-content', justifyContent: 'space-between' }}>
      <Stack direction='row' sx={{ ml: '0 !important', justifyContent: 'space-between', gap: 1 }}>
        <Stack direction='row' >
          <AttachMoney />
          <Typography>{FormatCurrency(details?.annualIncome ?? null, true)}</Typography>
        </Stack>
        <Stack direction='row'>
          <FamilyRestroom />
          <Typography>{details?.memberCount}</Typography>
        </Stack>
        <Stack direction='row'>
          <AccountBalanceIcon />
          <Typography>{details?.fplPercent}%</Typography>
        </Stack>
      </Stack>

      {props.canEdit && <IconButton sx={{ ml: '0 !important' }} size='small' color='inherit' onClick={editIncome}>
        <Edit fontSize='small' />
      </IconButton>}
    </Stack>

    {editMode && editDetails != null && <Modal
      cancelButtonText='Back'
      confirmButtonText='Save Income'
      titleContent={<div className='modalTitle'>{props.id == null ? 'Add' : 'Edit'} Reported Income</div>}
      confirmationContent={<>
        <TextFieldWithLabel name='annualIncome'
          label='Reported Income'
          type='number'
          min={0}
          toolTip='Income means total cash receipts before taxes from all sources, with the exception of student loans for tuition and books. If a parent is an active member of the U.S. Armed Forces allowances for living, moving, family, death, and in-kind benefits are exempt. Disability, group-term life insurances, uniform allowances, and survivor and retirement protection plan premiums are also exempt.'
          value={editDetails.annualIncome?.toString() ?? ''}
          onChange={(e) => {
            setEditDetails({ ...editDetails, annualIncome: Number(e.target.value) })
          }}
        />
        <TextFieldWithLabel name='memberCount'
          label='Household Members'
          type='number'
          min={1}
          toolTip='Family member is any individual living in the house that is related to the child&apos;s parent/guardian by blood, marriage, or adoption who are financially supported by the income of the parent or guardian of the child enrolling or participating in the program.'
          value={editDetails.memberCount?.toString() ?? ''}
          onChange={(e) => {
            setEditDetails({ ...editDetails, memberCount: Number(e.target.value) })
          }}
        />
      </>}
      onClose={closeModal}
      onCancel={closeModal}
      open={true}
      onConfirm={onUpdate}
      maxWidth='sm'
    />}
  </>
}
